import { useEffect, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useListTasks } from "@workspace/api-client-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search } from "lucide-react";

// Phase 41 Plan 09 — TASK-02: the one search entry point this phase ships.
// Staff paste a member-quoted number ("NJF-T-00147") or a bare sequence
// ("147") and land on the task. GET /tasks?number= (41-07) resolves either
// form, space-scoped, and returns zero or one row — never a list to page
// through (that's Queues, Phase 51/52, explicitly out of scope here).
export default function TaskSearchPage() {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);

  const { data: results, isFetching } = useListTasks(
    { number: submitted ?? "" },
    {
      query: {
        enabled: !!submitted,
        queryKey: ["/api/tasks", "search", submitted ?? ""],
      },
    },
  );

  useEffect(() => {
    if (submitted && results && results.length >= 1) {
      navigate(`/tasks/${results[0]!.id}`);
    }
  }, [results, submitted, navigate]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSubmitted(trimmed);
  }

  const notFound = submitted !== null && !isFetching && results !== undefined && results.length === 0;

  return (
    <div className="mx-auto max-w-md space-y-4">
      <div className="flex items-center gap-2">
        <Search className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Find a task</h1>
          <p className="text-sm text-muted-foreground">Paste a task number — full or bare.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Task number</CardTitle>
          <CardDescription>e.g. NJF-T-00147 or just 147</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="NJF-T-00147"
              autoFocus
            />
            <Button type="submit" disabled={isFetching || query.trim().length === 0}>
              {isFetching ? "Searching…" : "Search"}
            </Button>
          </form>

          {notFound && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>No task matches &quot;{submitted}&quot;.</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
